// src/common/components/settingsForm/useSettingsForm.ts (веб)

import { useCallback, useEffect } from 'react'
import { SettingsFormItem } from '../../../../../common/types/interfaces'
import { maxClusterSettingsCount } from '../../../../../common/utils/constants'

export const useSettingsForm = <T>(
  setData: React.Dispatch<React.SetStateAction<T>>,
  fieldName: string | number | symbol,
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  data: SettingsFormItem[],
) => {
  const addItem = useCallback(() => {
    setData((prev: any) => {
      if (prev[fieldName]?.length >= maxClusterSettingsCount) return prev

      const newItem: any = {
        id: Math.max(0, ...(prev[fieldName] || []).map((item: SettingsFormItem) => item.id)) + 1,
        level: '',
        value: '',
      }

      if (fieldName === 'delivery') {
        newItem.radius = ''
        newItem.fee = ''
        newItem.tag = ''
      }

      return {
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), newItem],
      }
    })
  }, [fieldName, setData])

  const handleInputChange = useCallback(
    (id: number, value: string, field: 'level' | 'value' | 'radius' | 'fee' | 'tag') => {
      setData((prev: any) => {
        const currentArray = prev[fieldName] || []
        const updated = currentArray.map((item: SettingsFormItem) => {
          if (item.id === id) {
            // ❌ tag — текст, остальные — только цифры
            if (field === 'tag') {
              return { ...item, tag: value.slice(0, 50) }
            }

            const numericOnly = value.replace(/\D/g, '')
            const maxLength = 10
            const validatedValue = numericOnly.slice(0, maxLength)
            return { ...item, [field]: validatedValue }
          }
          return item
        })
        return { ...prev, [fieldName]: updated }
      })
    },
    [fieldName, setData],
  )

  const sortItems = useCallback(() => {
    setData((prev: any) => {
      const currentArray = prev[fieldName] || []
      const sorted = [...currentArray].sort(
        (a, b) => (Number(a.level) || 0) - (Number(b.level) || 0),
      )
      return { ...prev, [fieldName]: sorted }
    })
  }, [fieldName, setData])

  const removeItem = useCallback(
    (id: number) => {
      setData((prev: any) => {
        const currentArray = prev[fieldName] || []
        const filtered = currentArray.filter((item: SettingsFormItem) => item.id !== id)
        return { ...prev, [fieldName]: filtered }
      })
    },
    [fieldName, setData],
  )

  useEffect(() => {
    const hasEmpty =
      data?.some((item) => {
        const isValueEmpty = item.value !== 0 && !item.value

        // Для бонусов — level обязателен
        if (fieldName !== 'delivery') {
          const isLevelEmpty = item.level !== 0 && !item.level
          return isLevelEmpty || isValueEmpty
        }

        // Для доставки — только value обязателен
        return isValueEmpty
      }) ?? false

    setDisabled(hasEmpty)
  }, [data, setDisabled, fieldName])

  return { addItem, handleInputChange, sortItems, removeItem }
}
